import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import PageTitle from '../components/PageTitle';
import { Link } from 'react-router-dom'; // 1. Added Link import

export default function Dashboard() {
  const { user, role } = useAuth();

  const [stats, setStats] = useState({
    users: '...',
    projects: '...', // 2. Added projects to initial state
    posts: '...',
    contacts: '...',
    applications: '...',
    visits: { allTime: '...', daily: '...', weekly: '...', monthly: '...', yearly: '...' },
    subscriptionAlerts: { expired: 0, sevenDays: 0, thirtyDays: 0 },
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // 3. Added projectSnap to the parallel fetch
        const [userSnap, projectSnap, postSnap, contactSnap, jobSnap, visitSnap, subscriptionSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'projects')), 
          getDocs(collection(db, 'posts')),
          getDocs(collection(db, 'contactForms')),
          getDocs(collection(db, 'jobs')),
          getDoc(doc(db, 'siteStats', 'visits')),
          getDocs(collection(db, 'customerSubscriptions')),
        ]);
        const remaining = subscriptionSnap.docs.map(d => d.data()).map(s => Math.ceil((new Date(s.expiryDate) - new Date()) / 86400000));

        let totalApplications = 0;
        for (const jobDoc of jobSnap.docs) {
          const appsSnap = await getDocs(collection(db, 'jobs', jobDoc.id, 'applications'));
          totalApplications += appsSnap.size;
        }

        setStats({
          users: userSnap.size,
          projects: projectSnap.size, // 4. Set project count
          posts: postSnap.size,
          contacts: contactSnap.size,
          applications: totalApplications,
          visits: visitSnap.exists() ? { allTime: visitSnap.data().count || 0, daily: visitSnap.data().daily || 0, weekly: visitSnap.data().weekly || 0, monthly: visitSnap.data().monthly || 0, yearly: visitSnap.data().yearly || 0 } : { allTime: 0, daily: 0, weekly: 0, monthly: 0, yearly: 0 },
          subscriptionAlerts: { expired: remaining.filter(d => d < 0).length, sevenDays: remaining.filter(d => d >= 0 && d <= 7).length, thirtyDays: remaining.filter(d => d >= 0 && d <= 30).length },
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error.message);
        setStats({
          users: 'Error', projects: 'Error', posts: 'Error', contacts: 'Error', applications: 'Error', visits: { allTime: 'Error', daily: 'Error', weekly: 'Error', monthly: 'Error', yearly: 'Error' }, subscriptionAlerts: { expired: 0, sevenDays: 0, thirtyDays: 0 }
        });
      }
    };

    fetchCounts();
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <PageTitle>⚡ Welcome, {user?.email}</PageTitle>
          
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            You are logged in as{' '}
            <strong className="text-warm-terracotta dark:text-warm-amber">
              {role?.toUpperCase()}
            </strong>.
          </p>
        </div>

        {/* Quick Stats Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
          <StatCard label="👥 Total Users" value={stats.users} accent="border-primary" />
          
          {/* 5. Added Manage Projects Link Card */}
          <Link to="/admin/manage-projects" className="block transform hover:scale-[1.02] transition-transform duration-200">
            <StatCard label="💼 Manage Projects" value={stats.projects} accent="border-blue-500" />
          </Link>

          <StatCard label="📰 Blog Posts" value={stats.posts} accent="border-warm" />
          <StatCard label="📬 Contact Forms" value={stats.contacts} accent="border-warm-amber" />
          <StatCard label="📝 Applications" value={stats.applications} accent="border-warm-terracotta" />
          <VisitorPeriods visits={stats.visits} />
        </div>
        <SubscriptionAlerts alerts={stats.subscriptionAlerts} />
      </div>
    </AdminLayout>
  );
}

/* ─────────── Reusable card component with Warm Accents ─────────── */
function StatCard({ label, value, accent }) {
  return (
    <div className={`bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border-l-4 ${accent} hover:shadow-md transition-all duration-300 h-full`}>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </h3>
      <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
        {value}
      </p>
    </div>
  );
}

function VisitorPeriods({ visits }) {
  const periods = [['All-time', visits.allTime], ['Daily', visits.daily], ['Weekly', visits.weekly], ['Monthly', visits.monthly], ['Yearly', visits.yearly]];
  return <div className="rounded-xl border-l-4 border-accent bg-white p-5 shadow-sm dark:bg-gray-800 sm:col-span-2 lg:col-span-3"><h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Visitor Periods</h3><div className="grid grid-cols-2 gap-3 sm:grid-cols-5">{periods.map(([label, value]) => <div key={label} className="rounded-lg bg-slate-50 p-3 dark:bg-gray-700"><p className="text-xs font-bold uppercase text-gray-500 dark:text-gray-300">{label}</p><p className="mt-1 text-2xl font-black text-gray-900 dark:text-white">{value}</p></div>)}</div></div>;
}

function SubscriptionAlerts({ alerts }) {
  return <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"><div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-lg font-bold">Subscription Notifications</h2><Link to="/admin/subscriptions" className="text-sm font-bold text-primary underline">View all</Link></div><div className="mt-4 grid gap-3 sm:grid-cols-3"><Alert label="Expired" value={alerts.expired} tone="text-red-600" /><Alert label="Expiring within 7 days" value={alerts.sevenDays} tone="text-amber-600" /><Alert label="Expiring within 30 days" value={alerts.thirtyDays} tone="text-primary" /></div></section>;
}
function Alert({ label, value, tone }) { return <div className="rounded-lg bg-slate-50 p-4 dark:bg-gray-700"><p className="text-xs font-bold uppercase text-gray-500 dark:text-gray-300">{label}</p><p className={`mt-1 text-3xl font-black ${tone}`}>{value}</p></div>; }
